import { useMemo, useState, useEffect, useRef, type ChangeEvent, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSession, sessionScope } from "@/lib/auth";
import { Button } from "@/components/ui/Button";
import { SlackImportApi, SlackImportApiError } from "./api";
import type { SlackImportJob } from "./types";

export function SlackImportWizard() {
  const session = useSession();
  return <SlackImportSession key={sessionScope(session)} />;
}

function SlackImportSession() {
  const [params, setParams] = useSearchParams();
  const savedJobId = params.get('slack_job');
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const api = useMemo(() => new SlackImportApi(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<SlackImportJob | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [channelMapping, setChannelMapping] = useState<Record<string, string>>({});
  const [ackPrivate, setAckPrivate] = useState(false);

  useEffect(() => {
    if (!savedJobId) return;
    let alive = true;
    void api.job(savedJobId).then(next => {
      if (!alive) return;
      setJob(next); setMapping(next.mapping ?? {});
      setSelected(new Set(next.selected_channel_ids ?? next.preview?.channels.filter(ch => ch.kind === 'public').map(ch => ch.slack_channel_id) ?? []));
    }).catch(error => { if (alive) setError(error.message); });
    return () => { alive = false; };
  }, [api, savedJobId]);

  useEffect(() => {
    if (!job || job.status !== 'running') return;
    let alive = true;
    const timer = setInterval(() => { void api.job(job.id).then(next => { if (alive) setJob(next); }).catch(error => { if (alive) setError(error.message); }); }, 2000);
    return () => { alive = false; clearInterval(timer); };
  }, [api, job?.id, job?.status]);

  const run = async (op: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await op();
    } catch (e) {
      setError(e instanceof SlackImportApiError || e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e: FormEvent) => {
    e.preventDefault();
    const input = (e.target as HTMLFormElement).elements.namedItem("archive") as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      setError("Slack export の ZIP または JSON を選んでください。");
      return;
    }
    await run(async () => {
      if (file.size > 20 * 1024 * 1024) throw new Error("20MB以下のexportを選んでください。");
      const res = await api.parse(file);
      if (!mounted.current) return;
      setJob(res.job);
      setParams(previous => { const next = new URLSearchParams(previous); next.set('source', 'slack'); next.set('slack_job', res.job.id); return next; });
      setSelected(new Set((res.job.preview?.channels ?? []).filter((c) => c.kind === "public").map((c) => c.slack_channel_id)));
    });
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const start = () =>
    run(async () => {
      if (!job) return;
      const res = await api.start(job.id, [...selected], mapping, ackPrivate, channelMapping);
      if (!mounted.current) return;
      setJob(res.job);
    });

  const preview = job?.preview;
  const needsAck = [...selected].some((id) => preview?.channels.find((c) => c.slack_channel_id === id)?.requires_explicit_ack);

  return (
    <section className="space-y-4" data-testid="slack-import-wizard">
      <header>
        <h2 className="text-lg font-bold">Slack export から取り込む</h2>
        <p className="text-sm text-fg-secondary">
          正当に取得した workspace export（JSON または ZIP、20MBまで）を解析します。公開チャンネルと、明示選択した private / DM のみ取り込みます。メール一致だけでは本人と結合しません。
        </p>
      </header>
      {error && <p role="alert" className="text-danger">{error}</p>}
      {!job && (
        <form onSubmit={onFile} className="space-y-3">
          <label className="block text-sm font-medium">
            Slack export ファイル
            <input name="archive" type="file" accept=".zip,.json,application/zip,application/json" className="mt-1 block" />
          </label>
          <Button type="submit" disabled={busy} loading={busy}>解析してプレビュー</Button>
        </form>
      )}
      {preview && job && ["pending", "validating", "previewing"].includes(job.status) && (
        <div className="space-y-4">
          <p>メッセージ {preview.messages_total} · チャンネル {preview.channels.length} · ファイル {preview.files_total}（ZIP内 {preview.files_in_archive} / URL {preview.files_with_url}）</p>
          <p className="text-sm text-fg-secondary">{preview.attachment_policy.not_fetched} 許可ホスト: {preview.attachment_policy.remote_hosts.join(", ")}</p>
          <ul className="space-y-2">
            {preview.channels.map((ch) => (
              <li key={ch.slack_channel_id}>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={selected.has(ch.slack_channel_id)} onChange={() => toggle(ch.slack_channel_id)} />
                  <span>{ch.name} ({ch.kind}) · {ch.message_count} 件</span>
                  <input aria-label={`${ch.name} の既存取り込み先ルームID`} className="border rounded px-2 py-1" placeholder="新しいルーム（任意で既存ID）" value={channelMapping[ch.slack_channel_id] ?? ''} onChange={event => setChannelMapping(previous => ({ ...previous, [ch.slack_channel_id]: event.target.value.trim() }))} />
                </label>
              </li>
            ))}
          </ul>
          <div>
            <h3 className="font-medium">ユーザー対応（任意・明示選択）</h3>
            <p className="text-sm text-fg-secondary">メール一致は候補表示のみです。結合する場合だけ ID を入力してください。</p>
            <ul className="space-y-2 mt-2">
              {preview.identities.map((id) => (
                <li key={id.slack_user_id} className="flex flex-wrap gap-2 items-center text-sm">
                  <span>{id.display_name} {id.email ? `<${id.email}>` : ""}</span>
                  {id.email_match_user_id && <span>候補 {id.email_match_user_id}</span>}
                  <input
                    aria-label={`${id.display_name} の結合先ユーザー ID`}
                    className="border rounded px-2 py-1"
                    value={mapping[id.slack_user_id] ?? ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setMapping((prev) => ({ ...prev, [id.slack_user_id]: e.target.value.trim() }))
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
          {needsAck && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={ackPrivate} onChange={(e) => setAckPrivate(e.target.checked)} />
              正当に export された private / DM を取り込むことを確認しました
            </label>
          )}
          <Button disabled={busy || selected.size === 0 || (needsAck && !ackPrivate)} onClick={() => void start()}>取り込み開始</Button>
        </div>
      )}
      {job && !["pending", "validating", "previewing"].includes(job.status) && (
        <div className="space-y-3">
          <p role="status">状態: {job.status} {job.progress.percent}%</p>
          <progress max={100} value={job.progress.percent} aria-label="Slack移行の進捗" />
          {job.failure_reason && <p role="alert" className="text-danger">{job.failure_reason}</p>}
          {job.first_room_id && <Link className="underline" to={`/rooms/${encodeURIComponent(job.first_room_id)}`}>取り込んだルームを開く</Link>}
          {job.dead_letters.length > 0 && (
            <div>
              <p>取り込み失敗 {job.dead_letters.length} 件</p>
              <ul>{job.dead_letters.map((d) => <li key={d.id}>{d.kind}: {d.slack_id} — {d.code ?? ""} {d.reason}</li>)}</ul>
              <Button disabled={busy} onClick={() => void run(async () => setJob(await api.retry(job.id)))}>再試行</Button>
            </div>
          )}
          {job.status === 'cancelled' && <Button disabled={busy} onClick={() => void run(async () => setJob(await api.retry(job.id)))}>続きから再試行</Button>}
          {job.status === "running" && <Button variant="secondary" disabled={busy} onClick={() => void run(async () => setJob(await api.cancel(job.id)))}>中止</Button>}
        </div>
      )}
    </section>
  );
}
